import api from './apiService';
import { authService } from './authService';

export interface ProductCategory {
  id: number;
  name: string;
}

export interface Product {
  buy_price: number;
  category_name: string;
  id: number;
  images: string[];
  is_active: boolean;
  is_rentable: boolean;
  is_sellable: boolean;
  name: string;
  price: number;
  product_type: string;
  rent_price: number;
  sku: string;
  slug: string;
  stock: number;
}

export interface ArenaProductsResponse {
  arena_name: string;
  arena_slug: string;
  products: Product[];
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

class ProductService {
  async fetchCategories(): Promise<ProductCategory[]> {
    try {
      const res = await api.get<ApiResponse<ProductCategory[]>>('/product-categories');
      return res.data.data || [];
    } catch (err) {
      console.error('Error fetching product categories', err);
      return [];
    }
  }

  async fetchArenaProducts(arenaSlug: string): Promise<ApiResponse<ArenaProductsResponse>> {
    try {
      const res = await api.get<ApiResponse<ArenaProductsResponse>>(`/products/${arenaSlug}`);
      return res.data;
    } catch (err) {
      console.error('Error fetching arena products', err);
      return { success: false, message: 'Failed to fetch products' };
    }
  }

  async createProduct(arenaSlug: string, payload: Record<string, any>, images: File[] = []) {
    try {
      const token = authService.getToken();
      const form = new FormData();

      // Append scalar values
      Object.keys(payload).forEach((k) => {
        const v = payload[k];
        if (v !== undefined && v !== null) {
          form.append(k, String(v));
        }
      });

      images.forEach((f) => form.append('images', f));

      const url = `/arenas/${arenaSlug}/products`;

      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await api.post<ApiResponse>(url, form, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      return res.data;
    } catch (error: any) {
      console.error('Error creating product', error);
      return { success: false, message: error?.response?.data?.message || error.message };
    }
  }

  async createService(arenaSlug: string, payload: Record<string, any>, images: File[] = []) {
    try {
      const token = authService.getToken();
      const form = new FormData();

      Object.keys(payload).forEach((k) => {
        const v = payload[k];
        if (v !== undefined && v !== null) {
          form.append(k, String(v));
        }
      });

      images.forEach((f) => form.append('images', f));

      const url = `/arenas/${arenaSlug}/services`;

      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await api.post<ApiResponse>(url, form, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      return res.data;
    } catch (error: any) {
      console.error('Error creating service', error);
      return { success: false, message: error?.response?.data?.message || error.message };
    }
  }

  async updateProduct(
    arenaSlug: string,
    productSlug: string,
    payload: Record<string, any>,
    images: File[] = [],
    keepImages: string[] = []
  ) {
    try {
      const token = authService.getToken();
      const form = new FormData();

      Object.keys(payload).forEach((k) => {
        const v = payload[k];
        if (v !== undefined && v !== null) {
          form.append(k, String(v));
        }
      });

      images.forEach((f) => form.append('images', f));

      // Add keep_images parameter for existing images to retain
      if (keepImages.length > 0) {
        form.append('keep_images', keepImages.join(','));
      } else {
        form.append('keep_images', '');
      }

      const url = `/arenas/${arenaSlug}/product/${productSlug}`;

      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await api.put<ApiResponse>(url, form, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      return res.data;
    } catch (error: any) {
      console.error('Error updating product', error);
      return { success: false, message: error?.response?.data?.message || error.message };
    }
  }

  async deleteProduct(arenaSlug: string, productSlug: string): Promise<ApiResponse> {
    try {
      const url = `/arenas/${arenaSlug}/product/${productSlug}`;
      const res = await api.delete<ApiResponse>(url);
      return res.data;
    } catch (error: any) {
      console.error('Error deleting product', error);
      return { success: false, message: error?.response?.data?.message || error.message };
    }
  }
}

export const productService = new ProductService();
